/**
 * Defensive mappers for the Events endpoints.
 *
 * Mapping only — no DOM, no fetching, no state. Everything the pages render is
 * resolved here so a view never reasons about a raw payload.
 *
 * Three things about the wire shape drive most of what follows:
 *
 *  1. **A recurring series shares ONE id.** The server expands a series into
 *     occurrences, so a single page can hold several rows whose `id` is the same
 *     and whose `occurrence_date` differs. {@link EventVM.key} (`id@date`) is the
 *     real identity; anything that dedupes on `id` silently drops all but the
 *     first occurrence — and `useServerPagedList` reads a page that added
 *     nothing new as the end of the data, so it would also stop paging.
 *  2. **The clock arrives in two places.** A current server sends the day in
 *     `start_date` and the time in its own `start_time` field; an older one put
 *     `"2026-06-20 14:00:00"` in `start_date`. Both are read — see
 *     {@link clockMinutePart} and the fallback in {@link mapEvent}.
 *  3. **RSVP has two vocabularies.** The modern nested `rsvp` object, and the
 *     legacy top-level `my_response` (`confirmed` / `rejected`). When the object
 *     is ABSENT the legacy pair is used, so the app keeps working against an
 *     older backend instead of showing every event as RSVP-disabled.
 *
 * The published OpenAPI only specifies the envelope, never field-level shapes,
 * so every read goes through `pick([...])` with several likely spellings. That
 * is deliberate; please keep it when editing.
 */

import { asRecord, pick, toList, domainBody, type Json } from './http';
import { fileNameFromUrl, formatBytes, isImageExtension } from '@/utils/file-kind';
import { civilDateOf, civilMs, civilMinutePart, clockMinutePart, addCivilDays } from './calendar';
import { uploadedFileUrl, personImageUrl } from './models';
import { initialsOf, formatRelative } from '@/utils/format';
import { htmlToText } from '@/utils/sanitize-html';
import { DEFAULT_LEAD, parseLead } from '@/services/event-reminders-plan';
import type {
  AgendaRowVM,
  AttendeeVM,
  EventCategory,
  EventDetailVM,
  EventFileVM,
  EventHostVM,
  EventRsvpVM,
  EventVM,
  ReminderLead,
  RsvpResultVM,
  RsvpStatus,
  VenueProvider,
} from '@/types/event';

// ── Primitive readers (the house defensive-mapper idiom) ─────────────────────

const str = (rec: Json, keys: string[], fallback = ''): string => {
  const value = pick<unknown>(rec, keys, fallback);
  return value == null ? fallback : String(value).trim();
};

const bool = (rec: Json, keys: string[], fallback = false): boolean => {
  const value = pick<unknown>(rec, keys, fallback);
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') return ['1', 'true', 'yes', 'on'].includes(value.toLowerCase());
  return fallback;
};

const num = (rec: Json, keys: string[], fallback = 0): number => {
  const value = Number(pick<unknown>(rec, keys, fallback));
  return Number.isFinite(value) ? value : fallback;
};

/** Was the key actually SENT? Distinct from "is it truthy" — see {@link mapEvent}. */
const declared = (rec: Json, keys: string[]): boolean =>
  keys.some((key) => rec[key] !== undefined && rec[key] !== null);

// ── Enum normalisation ───────────────────────────────────────────────────────

const CATEGORIES: readonly string[] = ['meeting', 'training', 'holiday', 'announcement', 'other'];

/** Anything the server sends that this app does not know about becomes `other`. */
function categoryOf(value: unknown): EventCategory {
  const text = String(value ?? '').trim().toLowerCase();
  return CATEGORIES.includes(text) ? (text as EventCategory) : 'other';
}

const VENUE_PROVIDERS: readonly string[] = ['', 'onsite', 'zoom', 'teams', 'google_meet', 'other'];

/** Spellings seen in the wild that mean one of the known providers. */
const VENUE_PROVIDER_ALIASES: Record<string, VenueProvider> = {
  microsoft_teams: 'teams',
  ms_teams: 'teams',
  meet: 'google_meet',
  googlemeet: 'google_meet',
  g_meet: 'google_meet',
  on_site: 'onsite',
  in_person: 'onsite',
  physical: 'onsite',
};

/**
 * Normalise the provider, or `''`.
 *
 * `''` and `'other'` are NOT the same thing and must not be collapsed: `other`
 * is a real server value meaning "some platform we don't name", while `''`
 * means the server said nothing at all — which is what the UI needs in order to
 * stay quiet rather than labelling an unspecified venue "Other".
 */
function venueProviderOf(value: unknown): VenueProvider {
  const text = String(value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!text) return '';
  if (VENUE_PROVIDERS.includes(text)) return text as VenueProvider;
  return VENUE_PROVIDER_ALIASES[text] ?? '';
}

/**
 * A join link, but ONLY when it is `http:` / `https:`.
 *
 * This value lands in an `href` the employee taps, so a `javascript:` (or
 * `data:`) URL from a compromised or careless organizer would run in the app's
 * origin. The value must also be printable ASCII end to end: a real link is,
 * and whitespace / control characters / look-alike Unicode are exactly how a
 * scheme check gets smuggled past.
 */
function safeJoinUrl(value: unknown): string {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text || !/^[!-~]+$/.test(text)) return '';
  return /^https?:\/\//i.test(text) ? text : '';
}

/**
 * The RSVP answer, or `null` when the person has not responded.
 *
 * Reads BOTH vocabularies — the modern `going` / `not_going` and the legacy
 * `confirmed` / `rejected` — so only one of them ever leaves this module.
 */
function rsvpStatusOf(value: unknown): RsvpStatus | null {
  const text = String(value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (!text || text === 'none' || text === 'null') return null;
  if (['going', 'confirmed', 'yes', 'accepted', 'attending'].includes(text)) return 'going';
  if (['not_going', 'rejected', 'declined', 'no', 'not_attending'].includes(text)) return 'not_going';
  return null;
}

/** Labels as an array, or as the comma-separated string the API also sends. */
function labelsOf(value: unknown): string[] {
  const out: string[] = [];
  const push = (item: unknown): void => {
    if (Array.isArray(item)) {
      item.forEach(push);
      return;
    }
    if (item && typeof item === 'object') {
      const name = str(asRecord(item), ['title', 'name', 'label']);
      if (name) out.push(name);
      return;
    }
    if (typeof item !== 'string') return;
    for (const part of item.split(',')) {
      const text = part.trim();
      if (text) out.push(text);
    }
  };
  push(value);
  return [...new Set(out)];
}

/**
 * Seat count, or `null`.
 *
 * Capacity is ADVISORY — it is displayed ("12 going / 40 seats") and never
 * blocks a response. A zero or negative capacity is treated as "uncapped"
 * rather than rendered, because "0 seats" on an event people are being invited
 * to reads as a fault in the app rather than in the data.
 */
function capacityOf(value: unknown): number | null {
  if (value == null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

// ── Time helpers ─────────────────────────────────────────────────────────────

/** Minutes past midnight as canonical `"HH:MM"`, or `''`. */
function clockText(minute: number): string {
  if (minute < 0) return '';
  const hours = String(Math.floor(minute / 60)).padStart(2, '0');
  const minutes = String(minute % 60).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/** Local midnight `ms` plus a wall-clock minute, as a local `Date`. */
function atMinute(dayMs: number, minute: number): Date {
  const at = new Date(dayMs);
  if (minute > 0) at.setHours(Math.floor(minute / 60), minute % 60, 0, 0);
  return at;
}

/**
 * Turn a value the server LABELS as UTC into something `toDate` reads as an
 * instant.
 *
 * `"2026-09-10 04:30:00"` has no zone, and both the spec and `toDate` therefore
 * read it as LOCAL — which in Manila makes a UTC timestamp eight hours wrong,
 * enough to turn "2h ago" into "10h ago". A value that already carries `Z` or
 * an offset is handed over untouched.
 */
function utcInstant(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const text = value.trim();
  if (!text) return '';
  if (/[Tt].*(?:[Zz]|[+-]\d{2}:?\d{2})$/.test(text)) return text;
  const match = text.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(?::\d{2})?)$/);
  if (!match) return text;
  return `${match[1]}T${match[2]}Z`;
}

// ── The event row ────────────────────────────────────────────────────────────

/**
 * One event row (list or detail header) → {@link EventVM}, or `null` when it
 * carries no usable start date.
 *
 * `null` rather than a placeholder: an event with no date cannot be placed on a
 * timeline, sorted, or reminded about, and a row that renders "Invalid Date" is
 * worse than a row that was never shown.
 */
export function mapEvent(raw: unknown, index = 0): EventVM | null {
  const record = asRecord(raw);

  const rawStart = pick<unknown>(record, ['start_date', 'start', 'start_at', 'starts_at'], '');
  const startDate = civilDateOf(rawStart);
  if (!startDate) return null;

  const rawEnd = pick<unknown>(record, ['end_date', 'end', 'end_at', 'ends_at'], '');
  const lastDay = civilDateOf(rawEnd) || startDate;
  // A malformed end before the start would produce a negative span; collapse it
  // rather than emitting an event that appears to finish before it begins.
  const endDate = civilMs(lastDay) >= civilMs(startDate) ? lastDay : startDate;

  // The day THIS row falls on. For a one-off it is the start date; for an
  // expanded recurring occurrence it is the only thing distinguishing this row
  // from its siblings, which all share the series' `id` and `start_date`.
  const occurrenceDate = civilDateOf(pick<unknown>(record, ['occurrence_date'], '')) || startDate;
  const occurrenceMs = civilMs(occurrenceDate);
  // `civilDateOf` accepts a well-FORMED date ("2026-02-31"); `civilMs` is what
  // rejects an impossible one. Without this guard `start` would be an Invalid
  // Date and `startMs` NaN, which quietly poisons every sort it takes part in.
  if (!occurrenceMs) return null;

  // Dedicated `start_time` / `end_time` first, then the legacy datetime that an
  // older backend embedded in `start_date`.
  const startFromField = clockMinutePart(pick<unknown>(record, ['start_time'], ''));
  const endFromField = clockMinutePart(pick<unknown>(record, ['end_time'], ''));
  const startMinute = startFromField >= 0 ? startFromField : civilMinutePart(rawStart);
  const endMinute = endFromField >= 0 ? endFromField : civilMinutePart(rawEnd);

  // An event with no time part at all IS all-day, whatever the flag says — the
  // alternative is rendering a start of "12:00 AM" that the organizer never set.
  const allDay = bool(record, ['all_day', 'is_all_day']) || startMinute < 0;

  // Shift the series' span onto this occurrence, so a two-day event repeated
  // weekly still covers two days on the week the reader is looking at.
  const startMs = civilMs(startDate);
  const endMs = civilMs(endDate);
  const spanDays = startMs && endMs ? Math.max(0, Math.round((endMs - startMs) / 86_400_000)) : 0;
  const occurrenceEndDate = spanDays ? addCivilDays(occurrenceDate, spanDays) : occurrenceDate;
  const occurrenceEndMs = civilMs(occurrenceEndDate);

  const start = atMinute(occurrenceMs, allDay ? -1 : startMinute);
  // `null` when the event says nothing about an end — neither a later day nor a
  // finish time. The UI reads that as "no end shown", not as "ends at midnight".
  const hasEnd = occurrenceEndDate !== occurrenceDate || (!allDay && endMinute >= 0);
  const end = hasEnd && occurrenceEndMs ? atMinute(occurrenceEndMs, allDay ? -1 : endMinute) : null;

  // ── RSVP ──
  const rawRsvp = pick<unknown>(record, ['rsvp'], null);
  const hasRsvpObject = !!rawRsvp && typeof rawRsvp === 'object' && !Array.isArray(rawRsvp);
  const rsvpRec = asRecord(rawRsvp);
  const legacyStatus = rsvpStatusOf(pick<unknown>(record, ['my_response', 'response'], ''));

  const rsvp: EventRsvpVM = hasRsvpObject
    ? {
        // Read STRICTLY. A malformed `rsvp: {}` resolving to "enabled" would put
        // Going / Not going buttons on an event the organizer switched RSVP off
        // for; resolving to "disabled" only hides a control, which is the calm
        // way to be wrong.
        enabled: bool(rsvpRec, ['enabled']),
        myStatus: rsvpStatusOf(pick<unknown>(rsvpRec, ['my_status', 'status'], '')) ?? legacyStatus,
        goingCount: num(rsvpRec, ['going_count', 'going', 'attendees_count', 'attendee_count']),
        capacity: capacityOf(pick<unknown>(rsvpRec, ['capacity', 'seats', 'max_attendees'], null)),
      }
    : {
        // No `rsvp` object at all means an OLDER backend, where every event was
        // RSVP-able and `my_response` was the whole story. Defaulting to
        // disabled here would hide RSVP across the entire module on that server.
        enabled: true,
        myStatus: legacyStatus,
        goingCount: num(record, ['going_count', 'attendees_count', 'attendees']),
        capacity: capacityOf(pick<unknown>(record, ['capacity', 'seats', 'max_attendees'], null)),
      };

  const hasEnded = bool(record, ['has_ended']);
  // When the server does not answer, derive it — an event nobody can respond to
  // is one where RSVP is off or the date has passed.
  const canRespond = declared(record, ['can_respond'])
    ? bool(record, ['can_respond'])
    : rsvp.enabled && !hasEnded;

  const hostRec = asRecord(pick<unknown>(record, ['host'], {}));
  const host: EventHostVM = {
    departmentId: str(hostRec, ['department_id', 'id']),
    departmentName: str(hostRec, ['department_name', 'name', 'title']),
    contactEmail: str(hostRec, ['contact_email', 'email']),
  };

  const description = str(record, ['description', 'details', 'body']);
  const id = str(record, ['id', 'uuid'], String(index)) || String(index);

  return {
    id,
    key: `${id}@${occurrenceDate}`,
    title: str(record, ['title', 'name', 'subject'], 'Event'),
    category: categoryOf(pick<unknown>(record, ['category', 'type'], '')),
    labels: labelsOf(pick<unknown>(record, ['labels', 'tags'], [])),

    startDate,
    endDate,
    startTime: allDay ? '' : clockText(startMinute),
    endTime: allDay ? '' : clockText(endMinute),
    allDay,

    start,
    end,
    startMs: start.getTime(),
    occurrenceDate,

    // `venue` is current; `location` is its legacy alias and is still sent, so
    // it must not win when both are present and disagree.
    venue: str(record, ['venue', 'location', 'place']),
    venueProvider: venueProviderOf(pick<unknown>(record, ['venue_provider', 'provider'], '')),
    joinUrl: safeJoinUrl(pick<unknown>(record, ['join_url', 'meeting_url', 'link'], '')),
    recurring: bool(record, ['recurring', 'is_recurring']),
    color: str(record, ['color']),

    organizerId: str(record, ['created_by', 'created_by_id', 'organizer_id']),
    organizerName: str(record, ['created_by_name', 'organizer_name', 'organizer']),
    organizerAvatar: uploadedFileUrl(
      pick<unknown>(record, ['created_by_avatar_url', 'created_by_avatar', 'organizer_avatar_url'], ''),
    ),
    host,

    rsvp,
    legacyResponse: rsvp.myStatus === 'going' ? 'confirmed' : rsvp.myStatus === 'not_going' ? 'rejected' : 'none',
    canRespond,
    hasEnded,

    attachmentsCount: num(record, ['attachments_count', 'files_count']),
    agendaCount: num(record, ['agenda_count']),
    description,
    // Derived only when the server did not send its own plain-text form, so a
    // preview never has to render (or strip) HTML at the call site.
    descriptionText: str(record, ['description_text', 'summary']) || htmlToText(description),
  };
}

/** A page of event rows. Rows with no usable start date are dropped, not faked. */
export function mapEvents(data: unknown): EventVM[] {
  const out: EventVM[] = [];
  toList(data).forEach((row, index) => {
    const mapped = mapEvent(row, index);
    if (mapped) out.push(mapped);
  });
  return out;
}

// ── Detail extras ────────────────────────────────────────────────────────────

/** Agenda rows, ordered by the server's `sort_order` and stable within ties. */
function mapAgenda(value: unknown): AgendaRowVM[] {
  const rows = toList(value)
    .map((raw, index) => {
      const rec = asRecord(raw);
      return {
        sort: num(rec, ['sort_order', 'order', 'position'], index),
        row: {
          // Pre-formatted by the server ("10:00 AM", or "Day 1" on a multi-day
          // offsite), so it is echoed rather than parsed — this app has no
          // business deciding an agenda line names a clock time at all.
          time: str(rec, ['time_label', 'time', 'start_time']),
          title: str(rec, ['title', 'label', 'name', 'activity']),
          owner: str(rec, ['owner', 'owner_name', 'speaker', 'presenter']),
        },
      };
    })
    .filter((entry) => entry.row.title || entry.row.time);

  rows.sort((a, b) => a.sort - b.sort);
  return rows.map((entry) => entry.row);
}

// Classification + size formatting live in the shared module so the file viewer
// and every attachment mapper agree on what an "image" is (src/utils/file-kind.ts).

/** Attachments. A row whose location will not resolve is dropped, not linked. */
function mapEventFiles(value: unknown): EventFileVM[] {
  const out: EventFileVM[] = [];
  toList(value).forEach((raw, index) => {
    const rec = asRecord(raw);
    const url = uploadedFileUrl(
      pick<unknown>(rec, ['url', 'file_url', 'download_url', 'path', 'file_path', 'file'], ''),
    );
    if (!url) return;

    const name = str(rec, ['name', 'file_name', 'original_name', 'title']) || fileNameFromUrl(url) || 'Attachment';
    const fromName = name.includes('.') ? (name.split('.').pop() ?? '') : '';
    const extension = (str(rec, ['extension', 'ext']) || fromName).replace(/^\./, '').toLowerCase();
    const sizeBytes = num(rec, ['size', 'size_bytes', 'file_size', 'bytes']);

    out.push({
      id: str(rec, ['id', 'uuid'], String(index)) || String(index),
      name,
      extension,
      sizeBytes,
      sizeLabel: formatBytes(sizeBytes),
      // Trust the server's own answer when it gives one; fall back to the
      // extension only when it does not.
      isImage: declared(rec, ['is_image']) ? bool(rec, ['is_image']) : isImageExtension(extension),
      url,
    });
  });
  return out;
}

/**
 * `GET /me/events/{id}` → {@link EventDetailVM}.
 *
 * Throws when the payload carries no usable event. A detail PAGE has nothing to
 * fall back to, and the thrown message reaches the user through `errorMessage`,
 * which is a better outcome than a page of empty fields.
 */
export function mapEventDetail(data: unknown): EventDetailVM {
  const rec = domainBody(data, ['event']);
  const base = mapEvent(rec);
  if (!base) throw new Error('This event could not be read. Please try again.');

  return {
    ...base,
    agenda: mapAgenda(pick<unknown>(rec, ['agenda', 'agenda_items'], [])),
    files: mapEventFiles(pick<unknown>(rec, ['files', 'attachments'], [])),
  };
}

// ── Attendees ────────────────────────────────────────────────────────────────

/** One roster row. */
export function mapAttendee(raw: unknown, index = 0): AttendeeVM {
  const rec = asRecord(raw);
  const userId = str(rec, ['user_id', 'id', 'uuid'], String(index)) || String(index);
  const name = str(rec, ['name', 'full_name', 'display_name']);

  // `responded_at_utc` is explicitly UTC and needs the zone restoring; any other
  // spelling is read as-is, because appending `Z` to a value the server did NOT
  // label UTC would shift a correct local timestamp.
  const respondedUtc = pick<unknown>(rec, ['responded_at_utc'], null);
  const respondedAtLabel =
    respondedUtc != null
      ? formatRelative(utcInstant(respondedUtc))
      : formatRelative(pick<unknown>(rec, ['responded_at', 'updated_at', 'created_at'], ''));

  return {
    id: userId,
    userId,
    name,
    image: personImageUrl(rec),
    initials: initialsOf(name),
    // The roster is overwhelmingly the going list, and a row the server chose to
    // return is a row it considers a response — so an unreadable status reads as
    // going rather than dropping the person.
    status: rsvpStatusOf(pick<unknown>(rec, ['status', 'my_status', 'response'], '')) ?? 'going',
    respondedAtLabel,
  };
}

/** A page of roster rows. */
export function mapAttendees(data: unknown): AttendeeVM[] {
  return toList(data).map((row, index) => mapAttendee(row, index));
}

// ── Respond + reminder preference ────────────────────────────────────────────

/**
 * `POST /me/events/{id}/respond` → the values the optimistic patch needs.
 *
 * The status is read back from the SERVER's answer rather than echoing what was
 * sent, so a server that clamped or rewrote the response (a closed RSVP racing
 * a tap) is what the UI ends up showing.
 */
export function mapRsvpResult(data: unknown): RsvpResultVM {
  const envelope = asRecord(data);
  const rec = domainBody(data, ['rsvp']);
  const status =
    rsvpStatusOf(pick<unknown>(rec, ['status', 'my_status'], '')) ??
    rsvpStatusOf(pick<unknown>(envelope, ['my_response'], '')) ??
    'going';
  return { status, goingCount: num(rec, ['going_count', 'going']) };
}

/**
 * Validate a lead against the grammar. An unreadable value falls back to the default.
 *
 * Falling back to `1d` rather than `off` mirrors `events_normalize_reminder_lead()`: a
 * preference we cannot read must never silently stop reminding somebody.
 */
function leadOf(value: unknown): ReminderLead {
  return parseLead(value)?.lead ?? DEFAULT_LEAD;
}

/**
 * `GET|PATCH /me/events/reminder-preference` → the lead.
 *
 * Accepts the value bare, under `reminder_preference`, or under `preference`,
 * and keyed `lead`, `event_reminder_lead` or `value` — the preference is a
 * single scalar, and every one of those spellings is a plausible envelope for
 * it. Anything unreadable becomes `1d`, the product default: a missing
 * preference must not silently mean "no reminders".
 *
 * The response also carries `options` / `units` / `max_minutes`, which this
 * deliberately DROPS. They describe the picker's vocabulary, and the client already
 * mirrors it as a hard constant (MAX_LEAD_MINUTES) because the same numbers drive
 * local scheduling — reading them here would give the app two sources for one fact and
 * let the in-app banner plan a lead the planner cannot honour.
 */
export function mapReminderLead(data: unknown): ReminderLead {
  if (typeof data === 'string') return leadOf(data);
  const rec = domainBody(data, ['reminder_preference', 'preference']);
  return leadOf(pick<unknown>(rec, ['lead', 'event_reminder_lead', 'value'], ''));
}
